import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, ArrowRight, ShieldCheck } from "lucide-react";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import PasswordStrength from "../components/PasswordStrength";
import PasswordInput from "../components/PasswordInput";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";

const redirectFor = (role) => (role === "admin" ? "/dashboard" : role === "client" ? "/client" : "/employee");

const SetPassword = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!user) navigate("/"); }, [user, navigate]);

  const go = () => navigate(redirectFor(user?.role));

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" }); return; }
    if (pw !== confirm) { toast({ title: "Passwords don't match", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { new_password: pw });
      updateUser && updateUser({ password_changed: true });
      toast({ title: "Password set", description: "You can now also sign in with your password." });
      go();
    } catch (err) {
      toast({ title: "Could not set password", description: err?.response?.data?.detail || "Try again.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50/60 px-6 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50"><KeyRound className="h-7 w-7 text-indigo-700" /></div>
          <h1 className="text-2xl font-bold text-[#1a1a2e]">Set a password</h1>
          <p className="mt-1 text-sm text-slate-500">You're signed in. Optionally create a password so you can sign in with it next time — or just keep using email codes.</p>
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-800">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" /> Signed in as <span className="font-semibold">{user.email}</span>
          </div>
          <form onSubmit={submit} className="mt-6 space-y-3">
            <div className="space-y-1.5"><Label>New password</Label><PasswordInput required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" data-testid="set-new-password" /><PasswordStrength password={pw} /></div>
            <div className="space-y-1.5"><Label>Confirm password</Label><PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid="set-confirm-password" /></div>
            <Button data-testid="set-password-submit" type="submit" disabled={busy} className="group w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
              {busy ? "Saving..." : "Save password"}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </form>
          <button data-testid="skip-password-btn" onClick={go} className="mx-auto mt-4 block text-sm font-medium text-slate-400 hover:text-slate-600">Skip for now — I'll use email codes</button>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
